import React, { useEffect, useRef, useState } from 'react';
import { BaileyStill } from '../agent/bailey/BaileyAvatar';
import { Composer } from '../agent/BaileyBits';
import { whoOf } from './inboxData';

const dayOf = (d) => new Date(d).toDateString();
const dayLabel = (d) => {
    const t = new Date(d); const now = new Date();
    const y = new Date(now); y.setDate(now.getDate() - 1);
    if (t.toDateString() === now.toDateString()) return 'Today';
    if (t.toDateString() === y.toDateString()) return 'Yesterday';
    return t.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
};
const timeOf = (d) => (d ? new Date(d).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }).replace(' ', '') : '');

/**
 * One conversation with Bailey, as the salon sees it: the client on the left,
 * Bailey and the team on the right, what Bailey checked as quiet lines, and a
 * box to step in. Writing takes the chat over (Bailey pauses in it) until
 * someone hands it back. Phone calls read only.
 */
export default function ReceptionThread({ convo, error, sending, onReply, onTakeover, assistantName = 'Bailey', look, compact = false }) {
    const [draft, setDraft] = useState('');
    const scroll = useRef(null);
    const count = convo ? convo.messages.length : 0;
    const convoId = convo ? convo._id : null;

    useEffect(() => {
        const el = scroll.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [count, convoId]);

    if (!convo) {
        return (
            <div className="rcp-thread is-empty">
                {error ? <p className="rcp-error" role="alert">{error}</p> : <p className="rcp-muted">Opening the conversation…</p>}
            </div>
        );
    }

    const who = whoOf(convo);
    const phone = convo.channel === 'voice';
    const held = !!(convo.takeover && convo.takeover.active);
    // Who has it: you, or someone else at the salon.
    const me = (() => { try { return localStorage.getItem('userId'); } catch (e) { return null; } })();
    const mine = held && (!convo.takeover.byUserId || String(convo.takeover.byUserId) === String(me));
    const send = async () => {
        const text = draft.trim();
        if (!text) return;
        if (await onReply(text)) setDraft('');
    };

    let lastDay = null;
    return (
        <div className={`rcp-thread${compact ? ' is-compact' : ''}`}>
            <div className="rcp-scroll" ref={scroll}>
                <div className="rcp-messages">
                    {convo.messages.map((m, i) => {
                        const day = m.at ? dayOf(m.at) : lastDay;
                        const sep = day && day !== lastDay ? <div className="rcp-day" key={`d${i}`}><span>{dayLabel(m.at)}</span></div> : null;
                        lastDay = day;
                        if (m.role === 'tool') {
                            return <React.Fragment key={i}>{sep}<div className="rcp-step">{m.text}</div></React.Fragment>;
                        }
                        const client = m.role === 'user';
                        const staff = m.role === 'staff';
                        const prev = convo.messages[i - 1];
                        const first = !prev || prev.role !== m.role || prev.role === 'tool' || !!sep;
                        return (
                            <React.Fragment key={i}>
                                {sep}
                                <div className={`rcp-msg ${client ? 'is-client' : staff ? 'is-staff' : 'is-bailey'}${first ? ' is-first' : ''}`}>
                                    {first && (
                                        <span className="rcp-who">
                                            {!client && !staff && <span className="rcp-face" aria-hidden="true"><BaileyStill appearance={look || undefined} face /></span>}
                                            {client ? who : staff ? (m.byName || 'The team') : assistantName}
                                            <small>{timeOf(m.at)}</small>
                                        </span>
                                    )}
                                    <div className="rcp-bubble">{m.text}</div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {error && <p className="rcp-error" role="alert">{error}</p>}

            {phone ? (
                <div className="rcp-banner is-quiet">A phone call. You can read what was said here; to reply, call {convo.clientName ? 'them' : 'the caller'} back.</div>
            ) : (
                <div className="rcp-foot">
                    <div className={`rcp-banner${held ? ' is-held' : ''}`}>
                        {held ? (
                            <>
                                <span><b>{mine ? 'You’re replying.' : `${(convo.takeover.byName || 'The team').split(' ')[0]} is replying.`}</b> {assistantName} is paused in this chat until it’s handed back.</span>
                                <button type="button" className="rcp-banner-btn" disabled={sending} onClick={() => onTakeover(false)}>Hand back to {assistantName}</button>
                            </>
                        ) : (
                            <>
                                <span><b>{assistantName} is answering.</b> Write below to step in; {assistantName} pauses in this chat until you hand it back.</span>
                                <button type="button" className="rcp-banner-btn" disabled={sending} onClick={() => onTakeover(true)}>Take over</button>
                            </>
                        )}
                    </div>
                    <div className="rcp-compose">
                        <Composer
                            variant="dock"
                            value={draft}
                            onChange={setDraft}
                            onSend={send}
                            busy={sending}
                            ariaLabel={`Message ${who}`}
                            placeholder={`Write to ${convo.clientName ? convo.clientName.split(' ')[0] : who.toLowerCase()}…`}
                        />
                        <small className="rcp-hint">They see it in the chat on your booking page, signed with your name.</small>
                    </div>
                </div>
            )}
        </div>
    );
}
